import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import Icon from './Icon';
import { useSmartTranslation } from '../hooks/useStudentLabel';
import useNavMenuItems from '../config/navMenu';

/**
 * Mobile Header (App Bar)
 * Shows page title and quick actions: Search, Notifications, Help.
 *
 * The menu lives in exactly one place — the bottom nav's "Menu" tab. This bar
 * used to carry a second hamburger that opened the very same drawer, which
 * left two identical menu buttons on screen at once.
 */
export default function MobileHeader({ onSearchClick, onNotificationClick, unreadNotifications = 0 }) {
  const { i18n } = useTranslation();
  const t = useSmartTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isRTL = i18n.language === 'he' || i18n.language === 'ar';

  const BackArrow = isRTL ? ArrowRight : ArrowLeft;

  // Match the route against the nav model rather than a hand-written list.
  // The old list named 15 paths and titled everything else "Dashboard" — now
  // that the drawer reaches Timetable, Reports, Birthday gifts and the rest,
  // that fallback would mislabel every one of them.
  const navItems = useNavMenuItems('mobile');
  const matched = navItems
    .filter((i) => location.pathname === i.path || location.pathname.startsWith(i.path + '/'))
    .sort((a, b) => b.path.length - a.path.length)[0];

  // Routes with no nav entry of their own, but which are still a page in their
  // own right rather than a detail view of one.
  const EXTRA_TITLES = [
    ['/account', 'header.myAccount'],
    ['/patients', 'sidebar.patients'],
    ['/checklists', 'sidebar.checklists'],
    ['/notes', 'sidebar.notes'],
    ['/student/', 'sidebar.students'],
    ['/teacher/', 'sidebar.teachers'],
  ];
  const extra = EXTRA_TITLES.find(([prefix]) => location.pathname.startsWith(prefix));

  // A sub-page is one whose route goes deeper than the page it belongs to —
  // that is what earns the back arrow and the smaller title.
  const isRoot = location.pathname === '/' || location.pathname === '/dashboard';
  const isSubPage = !isRoot && location.pathname !== matched?.path && !(extra && location.pathname === extra[0]);

  const getPageTitle = () => {
    if (isRoot) return t('sidebar.dashboard');
    if (matched) {
      return t(matched.labelKey, matched.labelFallback ? { defaultValue: matched.labelFallback } : undefined);
    }
    if (extra) return t(extra[1]);
    return t('sidebar.dashboard');
  };

  return (
    <header className="mobile-app-bar" style={{
      position: 'sticky',
      top: 0,
      left: 0,
      right: 0,
      height: isSubPage ? '48px' : '56px',
      // Grow the bar up into the status-bar / notch zone so the title row stays
      // clear of it in PWA-standalone and Capacitor (viewport-fit=cover makes
      // the inset non-zero on notched devices; 0px everywhere else).
      paddingTop: 'env(safe-area-inset-top, 0px)',
      boxSizing: 'content-box',
      backgroundColor: 'var(--color-surface)',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingInline: '16px',
      zIndex: 100,
      direction: isRTL ? 'rtl' : 'ltr'
    }}>
      {/* Back button for sub-pages */}
      {isSubPage && (
        <button
          onClick={() => navigate(-1)}
          aria-label={t('common.back', 'Back')}
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--color-text-primary, #1F2937)',
            marginInlineEnd: '4px',
            flexShrink: 0,
          }}
        >
          <BackArrow size={22} style={{ width: 22, minWidth: 22 }} />
        </button>
      )}

      {/* Page Title */}
      <h1 style={{
        fontSize: isSubPage ? '17px' : '20px',
        fontWeight: '700',
        color: 'var(--color-text-primary, #1F2937)',
        margin: 0,
        flex: 1,
        textAlign: isRTL ? 'right' : 'left',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {getPageTitle()}
      </h1>

      {/* Action Icons */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        {/* Search Icon */}
        <button
          onClick={onSearchClick}
          className="mobile-header-icon-btn"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            color: 'var(--mobile-text-secondary, #6B7280)'
          }}
          aria-label={t('common.search')}
        >
          <Icon name="search" style={{ fontSize: '20px' }} />
        </button>

        {/* Notification Bell */}
        <button
          onClick={onNotificationClick}
          className="mobile-header-icon-btn"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            color: 'var(--mobile-text-secondary, #6B7280)',
            position: 'relative'
          }}
          aria-label={t('header.notifications')}
        >
          <Icon name="notifications" style={{ fontSize: '20px' }} />
          {unreadNotifications > 0 && (
            <span style={{
              position: 'absolute',
              top: '6px',
              right: isRTL ? 'auto' : '6px',
              left: isRTL ? '6px' : 'auto',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#DC2626',
              border: '2px solid white'
            }} />
          )}
        </button>

        {/* Help Button */}
        <button
          onClick={() => navigate('/help')}
          className="mobile-header-icon-btn"
          style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transition: 'background-color 0.2s',
            color: 'var(--mobile-text-secondary, #6B7280)'
          }}
          aria-label={t('common.help')}
        >
          <Icon name="headset_mic" style={{ fontSize: '20px' }} />
        </button>

      </div>

      <style>{`
        .mobile-header-icon-btn:hover {
          background-color: rgba(0, 0, 0, 0.05);
        }

        .mobile-header-icon-btn:active {
          background-color: rgba(0, 0, 0, 0.1);
        }

        @media (min-width: 768px) {
          .mobile-app-bar {
            display: none;
          }
        }
      `}</style>
    </header>
  );
}
